import { ITEM_TYPES, itemUsesColor, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, qs, type ItemListRow, type ItemLookupResponse, type Paged } from '../api';
import { ColourPicker } from '../components/ColourPicker';
import { ItemImage } from '../components/ItemImage';
import { ItemRowLots } from '../components/ItemRowLots';
import { ItemsViewSwitch } from '../components/ItemsViewSwitch';
import { TYPE_LABEL } from '../lib/labels';
import { useSettled } from '../lib/useSettled';
import { useCategories, useColors } from '../queries';
import { Badge, Button, Card, Checkbox, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Pagination, Select, Spinner, number } from '../ui';

export function Items() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const itemType = (params.get('type') ?? '') as ItemType | '';
  const everything = params.get('all') === '1';
  const incoming = params.get('incoming') === '1';
  const page = Number(params.get('page') ?? 1) || 1;
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const list = useQuery({
    queryKey: ['items', q, itemType, everything, incoming, page],
    queryFn: () =>
      api<Paged<ItemListRow>>(`/api/v1/items${qs({ q, itemType, setUp: everything ? 'false' : undefined, incoming: incoming ? 'true' : undefined, page, pageSize: 50 })}`),
  });

  return (
    <>
      <PageHeader
        title="Items"
        subtitle="One row per item. Open a row for its colours, New and Used side by side; switch to By lot for one row per colour and condition."
        actions={
          <>
            <ItemsViewSwitch view="items" />
            <Link to="/items/new">
              <Button>New item</Button>
            </Link>
          </>
        }
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Search" className="w-64">
            <Input placeholder="Number or name" value={q} onChange={(e) => set({ q: e.target.value })} />
          </Field>
          <Field label="Type">
            <Select value={itemType} onChange={(e) => set({ type: e.target.value })}>
              <option value="">All types</option>
              {ITEM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </Field>
          <div className="space-y-1.5 pb-1.5">
            <Checkbox label="Only items with pieces on order" checked={incoming} onChange={(v) => set({ incoming: v ? '1' : null })} />
            <Checkbox label="Include catalogue items I have not set up" checked={everything} onChange={(v) => set({ all: v ? '1' : null })} />
          </div>
        </div>

        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorMessage error={list.error} />
        ) : list.data.rows.length === 0 ? (
          <Empty>
            {q || itemType || incoming
              ? 'No items match.'
              : everything
                ? 'The catalogue is empty.'
                : 'No items set up yet. Create one with New item, or place stock on order from the Purchases screen, and its items appear here.'}
          </Empty>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="data w-full min-w-[44rem] text-sm">
                <thead>
                  <tr>
                    <th className="w-8" />
                    <th>Item</th>
                    <th>Category</th>
                    <th className="text-right">Colours</th>
                    <th className="text-right">On hand</th>
                    <th className="text-right">On order</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.rows.map((r) => (
                    <Fragment key={r.id}>
                    <tr className="hover:bg-slate-50">
                      <td>
                        {r.variants > 0 && (
                          <button type="button" aria-expanded={open.has(r.id)} aria-label={`${open.has(r.id) ? 'Hide' : 'Show'} colours of ${r.itemNo}`} className="text-slate-500 hover:text-slate-900" onClick={() => toggle(r.id)}>
                            {open.has(r.id) ? '▾' : '▸'}
                          </button>
                        )}
                      </td>
                      <td>
                        <Link to={`/items/${r.id}`} className="flex items-center gap-3">
                          <ItemImage itemType={r.itemType} itemNo={r.itemNo} colorId={r.imageColorId ?? 0} />
                          <span>
                            <span className="block font-medium hover:underline">
                              {r.itemNo} {r.basic && <Badge tone="amber">basic record</Badge>}
                            </span>
                            <span className="block text-xs text-slate-500">
                              {r.name}
                              {r.itemType !== 'PART' && <> · {TYPE_LABEL[r.itemType]}</>}
                            </span>
                          </span>
                        </Link>
                      </td>
                      <td>{r.categoryName ?? <span className="text-slate-400">—</span>}</td>
                      <td className="text-right tabular-nums">{r.variants === 0 ? <Badge>not set up</Badge> : number(r.variants)}</td>
                      <td className="text-right tabular-nums">{number(r.onHand)}</td>
                      <td className="text-right tabular-nums">{r.onOrder > 0 ? <Badge tone="blue">{number(r.onOrder)} coming</Badge> : <span className="text-slate-400">0</span>}</td>
                    </tr>
                    {open.has(r.id) && (
                      <tr className="bg-slate-50">
                        <td />
                        <td colSpan={5} className="py-2">
                          <ItemRowLots itemId={r.id} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={(p) => set({ page: String(p) })} />
          </>
        )}
      </div>
    </>
  );
}
